import { Group, Text, Image, Stack } from "@mantine/core";
import type { BrandingSettings, LogoPositionToken } from "@livingsyncs/types";
import { LOGO_WIDTH, LOGO_HEIGHT, interpolate } from "./designTokens";

interface FormBrandingProps {
  settings: BrandingSettings;
  interpolationContext?: Record<string, string | undefined>;
}

const LIVINGSYNCS_WORDMARK = "LivingSyncs";

const JUSTIFY: Record<
  LogoPositionToken,
  React.CSSProperties["justifyContent"]
> = {
  "top-left": "flex-start",
  center: "center",
  right: "flex-end",
};

export function FormBranding({
  settings,
  interpolationContext = {},
}: FormBrandingProps) {
  const {
    logo,
    fallbackLogo = "livingsyncs",
    position = "top-left",
    logoWidth = "md",
    logoHeight = "md",
    showPoweredBy = false,
    poweredByText = "Powered by LivingSyncs",
  } = settings;

  // Resolve logo — may be a data URL or a {{variable}} placeholder
  const resolvedLogo = logo ? interpolate(logo, interpolationContext) : null;
  const hasCustomLogo =
    (resolvedLogo && resolvedLogo.startsWith("data:")) ||
    (resolvedLogo && resolvedLogo.startsWith("http"));

  const widthPx = LOGO_WIDTH[logoWidth];
  const heightPx = LOGO_HEIGHT[logoHeight];

  const logoElement = hasCustomLogo ? (
    <Stack gap={2} align={position === "center" ? "center" : "flex-start"}>
      <Image
        src={resolvedLogo!}
        w={widthPx}
        h={heightPx}
        fit="contain"
        alt="Organization logo"
      />
      {showPoweredBy && (
        <Text size="xs" c="dimmed">
          {poweredByText}
        </Text>
      )}
    </Stack>
  ) : fallbackLogo === "livingsyncs" ? (
    <Text size="lg" fw={700} c="brand" style={{ letterSpacing: "-0.02em" }}>
      {LIVINGSYNCS_WORDMARK}
    </Text>
  ) : null;

  if (!logoElement) return null;

  return (
    <Group
      justify={JUSTIFY[position]}
      mb="md"
      pb="md"
      style={{
        borderBottom: "1px solid var(--mantine-color-gray-2)",
      }}
    >
      {logoElement}
    </Group>
  );
}
